// Phase 2 — QTL Mapping (CIM-BIC + Churchill–Doerge permutation
// + bootstrap stability).
//
// Niranjan (Feb 2026) — Phase 2 wire-up.
//
// Three file inputs: genotype, phenotype, optional linkage map.
// Population-type dropdown drives the genetic model
// (RIL/DH → additive only; F2 → additive + dominance; BC1/BC2
// → combined a+d; Other → additive). Permutation slider with the
// "demo runs use 200, real analyses default 1000" copy. Synthetic-
// map opt-in checkbox + DEMO MAP watermark on results when active.
//
// `Phase2ResultPanels` is exported so /results/<id> renders the
// `molecular_qtl` section properly (interpretation strip, QTL table
// with all 9 fields, LOD profile per trait).
import React, { useState } from 'react';
import axios from 'axios';
import { toast } from 'sonner';
import {
  Upload, Loader2, CheckCircle2, ExternalLink, Download, FileText,
  AlertTriangle, FlaskConical,
} from 'lucide-react';
import { API } from '@/App';
import { sha256OfFile, recordAnalysis } from '@/utils/molecularSessionRegistry';
import { StepHeader, ParamLabel, RuntimeEstimate, useCsvHeader, autoSelectColumn, estimateRuntime } from './uploadCardHelpers';
import { HeritabilityGatingBanner, getPhase1TraitInfo } from './heritabilityGating';

const TIPS = {
  genotype_col: "Column in the phenotype file that holds the genotype/line ID. Must match the row index of the genotype file.",
  rep_col: "Column for replication blocks. If provided, BLUEs are computed via OLS (Genotype + Rep). Leave blank to use raw means.",
  population_type: "Drives the genetic model. RIL/DH → additive only. F2 → additive + dominance (2-df test). BC1/BC2 → combined (a+d) effect.",
  n_cofactors: "Max number of cofactor markers selected by stepwise BIC for the CIM background. Higher = more aggressive background control but slower.",
  window_cm: "Yu's window: cofactors within this cM distance of the test marker on the same chromosome are excluded from the background.",
  n_perm: "Permutations for Churchill–Doerge genome-wide LOD threshold. 200 is screening-grade; ≥ 1000 is paper-grade.",
  n_bootstrap: "Bootstrap reps for per-QTL stability score (% of bootstraps where the same peak survives within the window).",
  apply_marker_qc: "Apply Phase 1 marker QC (MAF ≥ 0.05, missingness ≤ 10%, het ≤ 50%) before mapping.",
};


const POPULATION_TYPES = [
  { value: 'F2',    label: 'F2 (additive + dominance)' },
  { value: 'BC1',   label: 'BC1 (combined a+d)' },
  { value: 'BC2',   label: 'BC2 (combined a+d)' },
  { value: 'RIL',   label: 'RIL F6+ (additive only)' },
  { value: 'DH',    label: 'Doubled Haploid (additive only)' },
  { value: 'Other', label: 'Other / Unknown (additive)' },
];


const MAP_FUNCTIONS = [
  { value: 'kosambi', label: 'Kosambi (1944) — accounts for crossover interference; Indian convention' },
  { value: 'haldane', label: 'Haldane (1919) — no interference assumed; international default for high-density maps' },
];


const METHODS = [
  { value: 'CIM', label: 'CIM — Composite Interval Mapping (gold standard; BIC cofactors)' },
  { value: 'SIM', label: 'SIM — Simple Interval Mapping (Lander-Botstein 1989; 1 cM scan)' },
  { value: 'SMA', label: 'SMA — Single-Marker Analysis (FDR, no interval)' },
];


/** Format Helper modal — shows side-by-side correct vs incorrect file
    layouts for the three QTL inputs so users don't get stuck on the
    formatting question. */
function FormatHelperModal({ open, onClose }) {
  if (!open) return null;
  const blocks = [
    {
      title: 'Genotype file', accent: '#6366F1',
      correct: [
        'Genotype,M01,M02,M03,M04',
        'RIL_001,0,1,0,2',
        'RIL_002,2,1,1,0',
        'RIL_003,1,0,2,1',
      ],
      incorrect: [
        'Sample,M01,M02,M03,M04',
        'RIL_001,AA,Aa,aa,AA   # ❌ allele codes',
        'RIL_002,2,1,1,?       # ❌ non-numeric',
        'RIL_003,1,0,2,1',
      ],
      rules: [
        'First column = Genotype ID (must match phenotype Genotype column).',
        'All marker cells must be numeric: 0 = ref homozygote, 1 = heterozygote, 2 = alt homozygote.',
        'Missing values blank or NaN (not "?" / "-").',
      ],
    },
    {
      title: 'Phenotype file', accent: '#0891B2',
      correct: [
        'Genotype,Rep,Yield_t_ha,Days_to_Flowering',
        'RIL_001,1,3.21,72',
        'RIL_001,2,3.05,71',
        'RIL_002,1,4.10,68',
      ],
      incorrect: [
        'Genotype,Yield,Days_to_Flowering',
        'RIL_001,3.21,72',
        'RIL_001,3.05,71      # ❌ duplicate Genotype rows without Rep',
        'RIL_002,"4,10",68     # ❌ comma decimal',
      ],
      rules: [
        'First column = Genotype (must match genotype file IDs).',
        'Optional Rep column for replicated trials (enables BLUE adjustment).',
        'Each numeric trait is a separate column. All other columns ignored.',
      ],
    },
    {
      title: 'Linkage map (optional)', accent: '#7C3AED',
      correct: [
        'marker,chromosome,position_cM',
        'M01,1,0.0',
        'M02,1,12.5',
        'M03,2,0.0',
        'M04,2,18.3',
      ],
      incorrect: [
        'name,chr,pos',
        'M01,Chr1,0.0     # ❌ wrong column name (must be "chromosome", not "chr")',
        'M02,Chr1,12.5     # ❌ wrong column name "pos" instead of "position_cM"',
        'M03,2,0          # ❌ position 0 with no preceding marker on same chr',
      ],
      rules: [
        'Required columns: marker, chromosome, position_cM.',
        'Optional: position_bp for physical position annotation.',
        'Distances given in cM (centiMorgans). Use Kosambi or Haldane mapping function setting below to document the conversion.',
      ],
    },
  ];
  return (
    <div
      className="fixed inset-0 z-50 flex items-center justify-center p-4"
      style={{ background: 'rgba(15,23,42,0.55)' }}
      data-testid="qtl-format-helper-modal"
      onClick={onClose}
    >
      <div
        className="bg-white rounded-2xl shadow-2xl max-w-5xl w-full max-h-[88vh] overflow-y-auto"
        onClick={(e) => e.stopPropagation()}
      >
        <div className="px-6 pt-5 pb-3 flex items-start justify-between border-b border-slate-200 sticky top-0 bg-white z-10">
          <div>
            <div className="text-base font-bold text-[#2C2A28]">QTL file format helper</div>
            <div className="text-xs text-slate-500 mt-0.5">
              Side-by-side correct vs incorrect examples for genotype, phenotype, and linkage map files.
            </div>
          </div>
          <button onClick={onClose} className="text-slate-500 hover:text-slate-900 text-xl leading-none px-2" data-testid="qtl-format-helper-close">×</button>
        </div>
        <div className="px-6 py-5 space-y-6">
          {blocks.map(b => (
            <div key={b.title}>
              <div className="text-sm font-bold mb-2" style={{ color: b.accent }}>{b.title}</div>
              <div className="grid grid-cols-1 md:grid-cols-2 gap-3">
                <div className="rounded-lg border border-emerald-200 bg-emerald-50/40 p-3" data-testid={`qtl-format-helper-correct-${b.title.split(' ')[0].toLowerCase()}`}>
                  <div className="text-[11px] font-bold text-emerald-700 uppercase tracking-wider mb-1">✓ Correct</div>
                  <pre className="text-[11px] font-mono whitespace-pre-wrap text-emerald-900">{b.correct.join('\n')}</pre>
                </div>
                <div className="rounded-lg border border-rose-200 bg-rose-50/40 p-3" data-testid={`qtl-format-helper-incorrect-${b.title.split(' ')[0].toLowerCase()}`}>
                  <div className="text-[11px] font-bold text-rose-700 uppercase tracking-wider mb-1">✗ Common mistakes</div>
                  <pre className="text-[11px] font-mono whitespace-pre-wrap text-rose-900">{b.incorrect.join('\n')}</pre>
                </div>
              </div>
              <ul className="mt-2 text-[11px] text-slate-600 list-disc pl-5 space-y-0.5">
                {b.rules.map((r, i) => <li key={i}>{r}</li>)}
              </ul>
            </div>
          ))}
          <p className="text-[11px] italic text-slate-500 pt-2 border-t border-slate-100">
            Tip: Click <strong>Try Sample Data</strong> below to auto-load a paper-grade synthetic
            dataset (200 RIL × 500 SNP × 10 chromosomes × 2 traits) that follows all conventions
            above. References: Lander &amp; Botstein 1989 (Genetics 121:185); Doerge 2002 (Nat Rev
            Genet 3:43); Singh &amp; Chaudhary 1985 §13.
          </p>
        </div>
      </div>
    </div>
  );
}


export function Phase2UploadCard({ onResult, analysisId, exporting, onExport }) {
  const [genoFile, setGenoFile] = useState(null);
  const [phenoFile, setPhenoFile] = useState(null);
  const [mapFile, setMapFile] = useState(null);
  const [genotypeCol, setGenotypeCol] = useState('Genotype');
  const [repCol, setRepCol] = useState('Rep');
  const [populationType, setPopulationType] = useState('RIL');
  const [nCofactors, setNCofactors] = useState(5);
  const [windowCm, setWindowCm] = useState(10);
  const [nPerm, setNPerm] = useState(1000);
  const [nBootstrap, setNBootstrap] = useState(200);
  const [applyQc, setApplyQc] = useState(true);
  const [allowSyntheticMap, setAllowSyntheticMap] = useState(false);
  const [mapFunction, setMapFunction] = useState('kosambi');
  const [method, setMethod] = useState('CIM');
  const [compareMethods, setCompareMethods] = useState(false);
  const [running, setRunning] = useState(false);
  const [loadingSample, setLoadingSample] = useState(false);
  const [result, setResult] = useState(null);
  const [isDemo, setIsDemo] = useState(false);
  const [showFormatHelper, setShowFormatHelper] = useState(false);

  // BATCH-NEXT-2 (Niranjan, Feb 2026): Phase 1 heritability bridge.
  const [phase1Summary, setPhase1Summary] = useState(null);
  const [proceedAnyway, setProceedAnyway] = useState(false);

  React.useEffect(() => {
    if (!genoFile) { setPhase1Summary(null); return; }
    let cancelled = false;
    sha256OfFile(genoFile).then(hash => {
      if (cancelled) return;
      setPhase1Summary(getPhase1TraitInfo(hash));
    });
    return () => { cancelled = true; };
  }, [genoFile]);

  const onPickGeno = (e) => { const f = e.target.files?.[0]; if (f) setGenoFile(f); };
  const onPickPheno = (e) => { const f = e.target.files?.[0]; if (f) setPhenoFile(f); };
  const onPickMap = (e) => { const f = e.target.files?.[0]; if (f) setMapFile(f); };

  const loadSampleData = async () => {
    setLoadingSample(true);
    try {
      const [g, p, m] = await Promise.all([
        axios.get(`${API}/molecular/qtl-sample-data/genotype`, { responseType: 'blob', withCredentials: true }),
        axios.get(`${API}/molecular/qtl-sample-data/phenotype`, { responseType: 'blob', withCredentials: true }),
        axios.get(`${API}/molecular/qtl-sample-data/map`, { responseType: 'blob', withCredentials: true }),
      ]);
      setGenoFile(new File([g.data], 'molecular_phase2_genotype.csv', { type: 'text/csv' }));
      setPhenoFile(new File([p.data], 'molecular_phase2_phenotype.csv', { type: 'text/csv' }));
      setMapFile(new File([m.data], 'molecular_phase2_map.csv', { type: 'text/csv' }));
      setPopulationType('RIL');
      setNPerm(200);
      setIsDemo(true);
      toast.success('Sample data loaded — 200 RIL lines × 500 SNPs × 10 chromosomes × 2 traits.');
    } catch {
      toast.error('Sample data fetch failed.');
    } finally {
      setLoadingSample(false);
    }
  };

  const runAnalysis = async () => {
    if (!genoFile || !phenoFile) {
      toast.error('Genotype and phenotype files are required.');
      return;
    }
    if (!mapFile && !allowSyntheticMap) {
      // SMA fallback path — confirm with user
      const proceed = window.confirm(
        'No linkage map uploaded. The pipeline will fall back to single-marker analysis (SMA) with FDR ' +
        'correction — QTL positions will be reported as "unknown". Tick "I understand this map is synthetic" ' +
        'to use a demo map instead. Proceed with SMA?'
      );
      if (!proceed) return;
    }
    setRunning(true);
    setResult(null);
    const fd = new FormData();
    fd.append('genotype_file', genoFile);
    fd.append('phenotype_file', phenoFile);
    if (mapFile) fd.append('map_file', mapFile);
    fd.append('genotype_col', genotypeCol);
    if (repCol && repCol.trim()) fd.append('rep_col', repCol.trim());
    fd.append('population_type', populationType);
    fd.append('n_cofactors', String(nCofactors));
    fd.append('window_cm', String(windowCm));
    fd.append('n_perm', String(nPerm));
    fd.append('n_bootstrap', String(nBootstrap));
    fd.append('apply_marker_qc', String(applyQc));
    fd.append('allow_synthetic_map', String(allowSyntheticMap));
    fd.append('is_demo', String(isDemo));
    fd.append('map_function', String(mapFunction));
    fd.append('method', String(method));
    fd.append('compare_methods', String(compareMethods));
    try {
      // Async job pattern (Jul 2026): long runs previously exceeded the
      // CDN's ~100 s origin-response cap in production — submit + poll.
      const { data: sub } = await axios.post(`${API}/molecular/qtl/jobs`, fd, {
        withCredentials: true,
        headers: { 'Content-Type': 'multipart/form-data' },
        timeout: 120000,
      });
      const jobId = sub.job_id;
      const t0 = Date.now();
      let data = null;
      let misses = 0;
      for (;;) {
        if (Date.now() - t0 > 20 * 60 * 1000) throw new Error('Timed out waiting for the QTL job to finish');
        await new Promise((r) => setTimeout(r, 3000));
        try {
          const { data: st } = await axios.get(`${API}/molecular/qtl/jobs/${jobId}`, {
            withCredentials: true, timeout: 30000,
          });
          misses = 0;
          if (st.status === 'failed') throw new Error(st.error || 'QTL job failed');
          if (st.status === 'done' && st.result) { data = st.result; break; }
        } catch (pe) {
          if (pe.message === 'QTL job failed' || (pe.message && !pe.request)) throw pe;
          misses += 1;
          if (misses >= 4) throw pe; // tolerate transient poll blips
        }
      }
      const r = data?.results?.molecular_qtl;
      setResult(r);
      onResult(data);
      try {
        const genotypeHash = await sha256OfFile(genoFile);
        // BATCH-NEXT-2 (Niranjan, Feb 2026): also persist
        // trait_architecture so the Phase 4 GS bridge can pick up
        // major QTL as fixed-effect candidates without a re-upload.
        recordAnalysis(data.id, {
          phase: 'qtl',
          genotypeHash,
          summary: { trait_architecture: r?.trait_architecture || null },
        });
      } catch { /* best-effort */ }
      toast.success('QTL Mapping complete!');
    } catch (e) {
      const msg = e.response?.data?.detail || e.message || 'Run failed';
      toast.error(`QTL Mapping failed: ${msg}`);
    } finally { setRunning(false); }
  };

  const phenoHeaders = useCsvHeader(phenoFile);

  // BATCH-NEXT-2: gating-banner support — computed once per render.
  const phase2SelectedTraits = React.useMemo(() => {
    return phenoHeaders.filter(h => {
      const lower = h.toLowerCase();
      return ![
        'rep', 'replication', 'replicate', 'block',
        genotypeCol.toLowerCase(), repCol.toLowerCase(),
      ].includes(lower);
    });
  // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [phenoHeaders.join(','), genotypeCol, repCol]);

  const phase2GatingBlocks = React.useMemo(() => {
    if (!phase1Summary?.traits || proceedAnyway) return false;
    const N = phase1Summary?.nIndividuals || 200;
    const threshold = Math.min(0.30, Math.max(0.10, 0.10 + 100 / N));
    return phase2SelectedTraits.some(t => {
      const r = phase1Summary.traits[t];
      return r && typeof r.h2 === 'number' && r.h2 < threshold;
    });
  }, [phase1Summary, proceedAnyway, phase2SelectedTraits]);

  // UI-FIX-2 (Niranjan, Feb 2026): auto-detect column when phenotype
  // header arrives. User overrides are preserved.
  React.useEffect(() => {
    if (!phenoHeaders.length) return;
    if (!phenoHeaders.includes(genotypeCol)) {
      const guess = autoSelectColumn(phenoHeaders, ['^genotype$', '^line$', '^entry$', '^id$']);
      if (guess) setGenotypeCol(guess);
    }
    if (repCol && !phenoHeaders.includes(repCol) && repCol !== '') {
      const guess = autoSelectColumn(phenoHeaders, ['^rep$', '^replication$', '^block$']);
      setRepCol(guess || '');
    }
  // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [phenoHeaders.join(',')]);

  return (
    <div className="space-y-5" data-testid="mol-phase2-card">
      <FormatHelperModal open={showFormatHelper} onClose={() => setShowFormatHelper(false)} />

      {/* STEP 1 — UPLOAD */}
      <div>
        <StepHeader step="1" title="Upload your data" hint="Genotype + phenotype + optional linkage map" />
        <div className="flex items-center justify-end mb-2">
          <button
            type="button"
            onClick={() => setShowFormatHelper(true)}
            className="flex items-center gap-1.5 text-[11px] font-semibold rounded-lg px-3 py-1.5 border border-[#6366F1]/40 text-[#6366F1] bg-[#6366F1]/5 hover:bg-[#6366F1]/10 transition-colors"
            data-testid="qtl-format-helper-open"
          >
            <FileText size={12} /> File format helper
          </button>
        </div>
        <div className="grid grid-cols-1 lg:grid-cols-3 gap-4">
          <FileSlot label="Genotype file (0/1/2 numeric matrix — diploid only)" file={genoFile} onPick={onPickGeno}
            testid="mol-phase2-genotype-file" accept=".csv,.xlsx,.xls"
            hint="Rows = lines; Cols = SNPs; Values in {0, 1, 2, NaN}. Any cell > 2 (polyploid dosage) will be refused at upload." />
          <FileSlot label="Phenotype file" file={phenoFile} onPick={onPickPheno}
            testid="mol-phase2-phenotype-file" accept=".csv,.xlsx,.xls"
            hint="Cols: Genotype | Rep (optional) | Trait1 | Trait2 …" />
          <FileSlot label="Linkage map (optional)" file={mapFile} onPick={onPickMap}
            testid="mol-phase2-map-file" accept=".csv,.xlsx,.xls"
            hint="Cols: marker, chromosome, position_cM, [position_bp]" />
        </div>
      </div>

      {/* STEP 2 — CONFIGURE */}
      <div>
        <StepHeader step="2" title="Configure analysis" />
        <div className="grid grid-cols-2 sm:grid-cols-4 gap-3 mb-3">
          <ColumnSelect label="Genotype column" value={genotypeCol} onChange={setGenotypeCol}
            options={phenoHeaders} testid="mol-phase2-genotype-col" hint={TIPS.genotype_col} />
          <ColumnSelect label="Rep column (opt.)" value={repCol} onChange={setRepCol}
            options={phenoHeaders} allowNone testid="mol-phase2-rep-col" hint={TIPS.rep_col} />
          <SelectInput label="Population type" value={populationType} onChange={setPopulationType}
            options={POPULATION_TYPES} testid="mol-phase2-population-type" hint={TIPS.population_type} />
          <SelectInput
            label="Map function"
            value={mapFunction}
            onChange={setMapFunction}
            options={MAP_FUNCTIONS}
            testid="mol-phase2-map-function"
            hint="Kosambi (1944, default) accounts for crossover interference and is the Indian convention. Haldane (1919) assumes no interference; preferred for high-density maps. The choice is documented in the methodology section of the output."
          />
          <SelectInput
            label="Method"
            value={method}
            onChange={setMethod}
            options={METHODS}
            testid="mol-phase2-method"
            hint="Choose the mapping method: SMA (single-marker, no interval), SIM (Simple Interval Mapping — Lander-Botstein 1989, 1 cM scan with flanking-marker conditional probabilities), or CIM (Composite Interval Mapping with BIC-selected cofactors — gold standard). All three use the same Churchill-Doerge permutation threshold."
          />
        </div>
        <div className="px-1">
          <label className="inline-flex items-center gap-2 text-[12.5px] text-slate-700 cursor-pointer mb-2">
            <input
              type="checkbox"
              checked={compareMethods}
              onChange={(e) => setCompareMethods(e.target.checked)}
              data-testid="mol-phase2-compare-methods"
              className="h-3.5 w-3.5 accent-[#4338CA]"
            />
            <span className="font-medium">Compare all three methods (SMA → SIM → CIM)</span>
            <span className="text-slate-400 italic">
              — overlays all three LOD curves on Figure B; ≈ +1 s/trait
            </span>
          </label>
        </div>
        <div className="px-1">
          <NumInput label="Cofactors (BIC)" value={nCofactors} setValue={setNCofactors}
            step={1} min={0} max={20} testid="mol-phase2-cofactors" hint={TIPS.n_cofactors} />
          <NumInput label="Window (cM)" value={windowCm} setValue={setWindowCm}
            step={1} min={1} max={50} testid="mol-phase2-window" hint={TIPS.window_cm} />
          <NumInput label="Permutations" value={nPerm} setValue={setNPerm}
            step={100} min={100} max={5000} testid="mol-phase2-nperm" hint={TIPS.n_perm} />
          <NumInput label="Bootstrap reps" value={nBootstrap} setValue={setNBootstrap}
            step={50} min={0} max={1000} testid="mol-phase2-nbootstrap" hint={TIPS.n_bootstrap} />
          <ToggleInput label="Marker QC" value={applyQc} setValue={setApplyQc}
            testid="mol-phase2-apply-qc" hint={TIPS.apply_marker_qc} />
        </div>

        {nPerm < 1000 && (
          <div className="rounded-lg px-3 py-2 text-xs flex items-center gap-2"
            style={{ background: '#FEF3C7', border: '1px solid #F59E0B40', color: '#92400E' }}
            data-testid="mol-phase2-perm-warning">
            <AlertTriangle size={13} />
            Threshold based on screening permutations (n_perm = {nPerm}). Re-run with n_perm ≥ 1000 before publication.
          </div>
        )}

        {!mapFile && (
          <label className="flex items-start gap-2 text-xs rounded-lg px-3 py-2.5 cursor-pointer transition-colors mt-2"
            style={{ background: allowSyntheticMap ? '#FEF3C7' : '#F8FAFC',
              border: `1px solid ${allowSyntheticMap ? '#F59E0B' : '#E2E8F0'}` }}
            data-testid="mol-phase2-synthetic-map-toggle">
            <input type="checkbox" checked={allowSyntheticMap}
              onChange={(e) => setAllowSyntheticMap(e.target.checked)}
              data-testid="mol-phase2-synthetic-map-checkbox" />
            <span>
              <strong>I understand this map is synthetic and QTL positions are arbitrary — for teaching/demo only.</strong>
              <span className="block text-slate-600 mt-0.5">
                Without a real linkage map, the pipeline falls back to single-marker analysis (SMA) + FDR.
                Tick to use a 1-cM synthetic map instead. Reports will carry a "DEMO MAP — NOT FOR PUBLICATION" watermark.
              </span>
            </span>
          </label>
        )}

        <div className="text-[11px] text-slate-500 mt-2" data-testid="mol-phase2-perm-help">
          Demo runs use 200 permutations for speed. Real analyses default to 1000 (paper-grade).
        </div>

        <HeritabilityGatingBanner
          phase="qtl"
          selectedTraits={phase2SelectedTraits}
          phase1Summary={phase1Summary}
          nIndividualsHint={phase1Summary?.nIndividuals || 200}
          proceedAnyway={proceedAnyway}
          onProceedAnyway={setProceedAnyway}
          testid="mol-phase2-h2-gating"
        />
      </div>

      {/* STEP 3 — RUN */}
      <div>
        <StepHeader step="3" title="Run analysis" />
        <div className="flex flex-wrap items-center gap-2">
          <button type="button" onClick={loadSampleData} disabled={loadingSample || running}
            className="flex items-center gap-1.5 text-xs font-semibold rounded-lg px-3.5 py-2 border border-[#6366F1]/30 text-[#6366F1] bg-[#6366F1]/5 hover:bg-[#6366F1]/10 disabled:opacity-50"
            data-testid="mol-phase2-sample-data">
          {loadingSample ? <Loader2 size={13} className="animate-spin" /> : <Download size={13} />} Try Sample Data
          </button>
          <button type="button" onClick={runAnalysis} disabled={running || !genoFile || !phenoFile || phase2GatingBlocks}
            className="flex items-center gap-1.5 text-sm font-semibold rounded-lg px-4 py-2 bg-[#6366F1] text-white hover:bg-[#4F46E5] disabled:opacity-50 shadow-sm"
            data-testid="mol-phase2-run">
            {running ? <Loader2 size={14} className="animate-spin" /> : <Upload size={14} />}
            {running ? `Running CIM-BIC + ${nPerm} perm…` : 'Run QTL Mapping'}
          </button>
          <RuntimeEstimate testid="mol-phase2-runtime-estimate"
            estimate={estimateRuntime({
              phase: 'qtl',
              nIndiv: isDemo ? 200 : 200,
              nMarkers: 500,
              nPerm,
              nBootstrap,
            })} />
        </div>
      </div>

      {result && (
        <div className="space-y-5 fade-in" data-testid="mol-phase2-result">
          <div className="flex items-center justify-between gap-3 rounded-xl px-4 py-3"
            style={{ background: '#6366F110', border: '1px solid #6366F130' }}
            data-testid="analysis-complete-banner">
            <div className="flex items-center gap-3 min-w-0">
              <div className="flex items-center justify-center rounded-full shrink-0" style={{ width: 28, height: 28, background: '#6366F1', color: 'white' }}>
                <CheckCircle2 size={16} />
              </div>
              <div className="min-w-0">
                <div className="truncate" style={{ fontSize: 13, fontWeight: 700, color: '#6366F1' }}>QTL Mapping complete</div>
                <div className="truncate" style={{ fontSize: 11, color: '#5E5A56' }}>Open the dedicated Results page for the QTL table, LOD plots, and downloads.</div>
              </div>
            </div>
            {analysisId && (
              <button onClick={() => window.location.href = `/results/${analysisId}`}
                className="flex items-center gap-1.5 shrink-0 transition-all"
                style={{ fontSize: 13, fontWeight: 700, borderRadius: 8, padding: '8px 18px',
                  border: '1px solid #6366F1', color: 'white', background: '#6366F1',
                  boxShadow: '0 2px 6px #6366F140' }}
                data-testid="view-full-results">
                <ExternalLink size={13} /> View Full Results
              </button>
            )}
          </div>

          <Phase2ResultPanels result={result} />

          {analysisId && onExport && (
            <div className="flex items-center gap-2 flex-wrap pt-2" data-testid="export-bar">
              <span className="text-[11px] font-semibold uppercase tracking-wider text-slate-500 mr-1">Export</span>
              {['docx', 'xlsx', 'pdf'].map(fmt => (
                <button key={fmt} onClick={() => onExport(analysisId, fmt)} disabled={!!exporting}
                  className="flex items-center gap-1.5 text-[11px] font-semibold rounded-full px-3 py-1.5 transition-all border shadow-sm disabled:opacity-50 border-[#6366F1]/30 text-[#6366F1] bg-[#6366F1]/5 hover:bg-[#6366F1]/10"
                  data-testid={`export-${fmt}-button`}>
                  {exporting === `${analysisId}-${fmt}` ? <Loader2 size={11} className="animate-spin" /> : <Download size={11} />}
                  {fmt.toUpperCase()}
                </button>
              ))}
            </div>
          )}
        </div>
      )}
    </div>
  );
}


// ───────────────────── Sub-components ─────────────────────────────

function FileSlot({ label, file, onPick, testid, accept, hint }) {
  return (
    <label className="block rounded-xl border-2 border-dashed border-[#E8E6E1] hover:border-[#6366F1] p-4 cursor-pointer transition-colors bg-white" data-testid={testid}>
      <div className="flex items-center gap-2 mb-1.5">
        <FileText size={14} className="text-[#6366F1]" />
        <span className="text-xs font-semibold text-slate-700">{label}</span>
      </div>
      <input type="file" accept={accept} onChange={onPick} className="hidden" />
      <div className="text-xs text-slate-500 mb-2">{hint}</div>
      <div className="flex items-center gap-2 mt-2">
        {file ? (
          <span className="flex items-center gap-1.5 text-[11px] px-2 py-1 rounded bg-[#6366F1]/10 text-[#6366F1] font-mono truncate max-w-full">
            <CheckCircle2 size={11} /> {file.name} ({(file.size / 1024).toFixed(1)} KB)
          </span>
        ) : (
          <span className="text-[11px] text-slate-400 italic">Click to select a CSV / XLSX file</span>
        )}
      </div>
    </label>
  );
}


function ColInput({ label, value, onChange, testid, hint }) {
  return (
    <div className="block">
      <ParamLabel label={label} hint={hint} testid={testid} htmlFor={testid} />
      <input id={testid} type="text" value={value} onChange={(e) => onChange(e.target.value)}
        className="w-full text-sm border border-slate-200 rounded-md px-2 py-1.5 focus:outline-none focus:ring-2 focus:ring-[#6366F1]/30"
        data-testid={testid} />
    </div>
  );
}


function ColumnSelect({ label, value, onChange, options, allowNone, testid, hint }) {
  // UI-FIX-2 (Niranjan, Feb 2026): when CSV header has been parsed
  // we render a <select>; before that or for free-form input we
  // fall back to a text field so the user can still type a column
  // name that doesn't appear yet.
  if (!options || options.length === 0) {
    return <ColInput label={label} value={value} onChange={onChange} testid={testid} hint={hint} />;
  }
  return (
    <div className="block">
      <ParamLabel label={label} hint={hint} testid={testid} htmlFor={testid} />
      <select id={testid} value={value} onChange={(e) => onChange(e.target.value)}
        className="w-full text-sm border border-slate-200 rounded-md px-2 py-1.5 focus:outline-none focus:ring-2 focus:ring-[#6366F1]/30 bg-white"
        data-testid={testid}>
        {allowNone && <option value="">(none)</option>}
        {options.map(o => <option key={o} value={o}>{o}</option>)}
        {value && !options.includes(value) && (
          <option value={value}>{value} (custom)</option>
        )}
      </select>
    </div>
  );
}


function NumInput({ label, value, setValue, step, min, max, testid, hint }) {
  return (
    <div className="block">
      <ParamLabel label={label} hint={hint} testid={testid} htmlFor={testid} />
      <input id={testid} type="number" value={value}
        onChange={(e) => setValue(parseFloat(e.target.value) || min)}
        step={step} min={min} max={max}
        className="w-full text-sm border border-slate-200 rounded-md px-2 py-1.5 focus:outline-none focus:ring-2 focus:ring-[#6366F1]/30"
        data-testid={testid} />
    </div>
  );
}


function ToggleInput({ label, value, setValue, testid, hint }) {
  return (
    <div className="block">
      <ParamLabel label={label} hint={hint} testid={testid} htmlFor={testid} />
      <button id={testid} type="button" onClick={() => setValue(!value)}
        className={`w-full text-sm border rounded-md px-2 py-1.5 text-left transition-colors ${value ? 'border-[#6366F1] bg-[#6366F1]/10 text-[#6366F1] font-semibold' : 'border-slate-200 bg-white text-slate-500'}`}
        data-testid={testid}>
        {value ? '✓ Enabled' : '✗ Disabled'}
      </button>
    </div>
  );
}


function SelectInput({ label, value, onChange, options, testid, hint }) {
  return (
    <div className="block">
      <ParamLabel label={label} hint={hint} testid={testid} htmlFor={testid} />
      <select id={testid} value={value} onChange={(e) => onChange(e.target.value)}
        className="w-full text-sm border border-slate-200 rounded-md px-2 py-1.5 focus:outline-none focus:ring-2 focus:ring-[#6366F1]/30 bg-white"
        data-testid={testid}>
        {options.map(o => <option key={o.value} value={o.value}>{o.label}</option>)}
      </select>
    </div>
  );
}


// ─────────────────── Result panel ────────────────────────────────

export function Phase2ResultPanels({ result }) {
  const [expandedQtl, setExpandedQtl] = React.useState(null);

  // BATCH-NEXT-3 (Niranjan, Feb 2026): "Promote to Fixed-QTL" eligibility
  // — at least one trait with class ∈ {oligogenic, mixed} AND at least
  // one major QTL with PVE ≥ 5 %.
  const promoteEligible = React.useMemo(() => {
    const arch = result?.trait_architecture || {};
    return Object.values(arch).some(a => {
      if (!a) return false;
      const okClass = ['oligogenic', 'mixed'].includes(String(a.class || '').toLowerCase());
      const peaks = a.qtl_peaks || [];
      const hasMajor = peaks.some(q => (q?.pve_percent ?? 0) >= 5);
      return okClass && hasMajor;
    });
  }, [result?.trait_architecture]);

  if (!result) return null;
  const summary = result.input_summary || {};
  const method = result.method_summary || {};
  const perTrait = result.per_trait || {};
  const profileCharts = result.lod_profile_charts || {};

  return (
    <div className="space-y-5" data-testid="mol-phase2-panels">
      {method.synthetic_map_used && (
        <div className="rounded-xl px-4 py-3 text-sm font-semibold flex items-center gap-2"
          style={{ background: '#FEF2F2', border: '1px solid #FCA5A5', color: '#7F1D1D' }}
          data-testid="mol-phase2-watermark-banner">
          <AlertTriangle size={14} />
          DEMO MAP — NOT FOR PUBLICATION. QTL positions are synthetic and arbitrary.
        </div>
      )}
      {method.screening_grade_perm && (
        <div className="rounded-xl px-4 py-2.5 text-xs flex items-center gap-2"
          style={{ background: '#FEF3C7', border: '1px solid #F59E0B40', color: '#92400E' }}
          data-testid="mol-phase2-screening-banner">
          <AlertTriangle size={13} />
          Threshold based on screening permutations (n_perm = {method.n_permutations}). Re-run with n_perm ≥ 1000 before publication.
        </div>
      )}
      {method.segregation_warning && (
        <div className="rounded-xl px-4 py-2.5 text-xs flex items-center gap-2"
          style={{ background: '#FEF3C7', border: '1px solid #F59E0B40', color: '#92400E' }}
          data-testid="mol-phase2-segregation-warning">
          <AlertTriangle size={13} />
          {method.segregation_warning}
        </div>
      )}

      {/* Sub-Batch 3AK (Niranjan, Feb 9 2026): user-facing marker-QC
          warning. The Sub-Batch 3AJ validation finding on R/qtl
          `hyper` showed BKAgriD's default `missingness ≤ 10%` filter
          drops 87 % of markers on RFLP-era datasets. Silently
          analysing 13 % of the file is the failure mode this banner
          prevents — the breeder sees the count, sees the cause,
          chooses whether to re-run with a different threshold or
          continue knowing what's been filtered. Two tiers so the
          urgency signal is proportional. */}
      {(() => {
        const qc = result.marker_qc_summary || {};
        const level = qc.warning_level;
        const msg = qc.warning_message;
        if (!level || level === 'none' || !msg) return null;
        const isSevere = level === 'severe';
        return (
          <div
            className="rounded-xl px-4 py-3 text-xs flex items-start gap-2 leading-relaxed"
            style={{
              background: isSevere ? '#FEF2F2' : '#FEF3C7',
              border: `1px solid ${isSevere ? '#FCA5A5' : '#F59E0B40'}`,
              color: isSevere ? '#7F1D1D' : '#92400E',
            }}
            data-testid={`mol-phase2-marker-qc-warning-${level}`}
          >
            <AlertTriangle size={14} style={{ marginTop: 2, flexShrink: 0 }} />
            <div>
              <div style={{ fontWeight: 700, marginBottom: 4, textTransform: 'uppercase', letterSpacing: '0.04em' }}>
                Marker QC — {isSevere ? 'severe filtering' : 'moderate filtering'}
              </div>
              <div>{msg}</div>
              <div style={{ marginTop: 6, fontSize: 11, opacity: 0.85 }}>
                <strong>{qc.n_markers_retained}</strong> of <strong>{qc.n_markers_input}</strong> markers
                retained ({((1 - (qc.drop_rate || 0)) * 100).toFixed(0)} %).
                Breakdown: <strong>{qc.n_filtered_maf || 0}</strong> dropped for MAF &lt; {(qc.thresholds?.maf_min * 100 || 5).toFixed(0)} %,
                <strong> {qc.n_filtered_missing || 0}</strong> for missingness &gt; {(qc.thresholds?.missingness_max * 100 || 10).toFixed(0)} %,
                <strong> {qc.n_filtered_het || 0}</strong> for heterozygosity &gt; {(qc.thresholds?.heterozygosity_max * 100 || 50).toFixed(0)} %.
              </div>
            </div>
          </div>
        );
      })()}

      {result.interpretation && (
        <div className="rounded-xl border border-[#6366F1]/30 bg-[#EEF2FF] px-4 py-3 text-sm text-slate-700 leading-relaxed" data-testid="mol-phase2-interpretation">
          {result.interpretation}
        </div>
      )}

      {/* Sub-Batch 3AC P1 (+ 3AD refactor): Linkage Map
          Visualization. Renders ONLY when a real user-uploaded map
          is present — never for the SMA fallback (no positions) or
          the synthetic-map path (keeps existing "DEMO MAP — NOT FOR
          PUBLICATION" watermark on the LOD profile chart).

          3AD: two figures shipped —
            (A) Structural linkage map (chromosome bars + markers
                + QTL coloured bars by trait)
            (B) LOD profile with permutation threshold, 1.5-LOD
                support shading, labelled peak dots.
       */}
      {result.linkage_map_figure && result.linkage_map_figure.chart && (
        <div className="rounded-2xl border border-[#E8E6E1] overflow-hidden" data-testid="mol-phase2-linkage-map">
          <div className="bg-[#6366F1]/5 px-4 py-2.5 border-b border-[#E8E6E1]">
            <h4 className="font-semibold text-sm text-[#4338CA] uppercase tracking-wider">
              Figure A · Genetic Linkage Map
            </h4>
            <p className="text-[11px] text-slate-500 mt-0.5 leading-relaxed">
              {result.linkage_map_figure.description}
            </p>
          </div>
          <img
            src={`data:image/png;base64,${result.linkage_map_figure.chart}`}
            alt="Genetic linkage map with QTL overlays"
            className="w-full"
            data-testid="mol-phase2-linkage-map-img"
          />
        </div>
      )}

      {result.linkage_map_figure && result.linkage_map_figure.lod_profile_chart && (
        <div className="rounded-2xl border border-[#E8E6E1] overflow-hidden" data-testid="mol-phase2-lod-profile">
          <div className="bg-[#E69F00]/10 px-4 py-2.5 border-b border-[#E8E6E1]">
            <h4 className="font-semibold text-sm text-[#92400E] uppercase tracking-wider">
              Figure B · LOD Profile &amp; Support Intervals
            </h4>
            <p className="text-[11px] text-slate-500 mt-0.5 leading-relaxed">
              {result.linkage_map_figure.lod_profile_description}
            </p>
          </div>
          <img
            src={`data:image/png;base64,${result.linkage_map_figure.lod_profile_chart}`}
            alt="LOD profile with permutation threshold and support intervals"
            className="w-full"
            data-testid="mol-phase2-lod-profile-img"
          />
        </div>
      )}

      {/* Sub-Batch 3AE (Feb 8 2026): SMA → SIM → CIM comparison
          panel. Renders only when the user toggled "Compare all
          three methods". Each trait gets three overlaid LOD
          curves on the same x-axis so MSc/PhD students can SEE
          the SMA→SIM→CIM progression: SMA gives spiky per-marker
          LOD; SIM smooths it via flanking-marker conditional
          information; CIM tightens further by regressing out
          background QTL on other chromosomes. */}
      {result.per_trait && (
        <Phase2MethodsComparisonPanel perTrait={result.per_trait} />
      )}

      <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
        <SummaryCard title="Input" testid="mol-phase2-input-summary" rows={[
          ['Individuals', summary.n_individuals],
          ['Markers', summary.n_markers],
          ['Trait columns', (summary.trait_cols || []).join(', ') || '—'],
          ['Population type', method.population_type],
          ['Cofactors max', summary.n_cofactors_max],
          ['Window (cM)', summary.window_cm],
          ['Permutations', method.n_permutations],
          ['Bootstrap', method.n_bootstrap],
          ['Phenotype adjustment', method.phenotype_adjustment],
        ]} />
        <SummaryCard title="Method" testid="mol-phase2-method-summary" rows={[
          ['Mapping method', method.mapping_method],
          ['Fallback (no map)', method.fallback_when_no_map],
          ['Linkage map provided', method.linkage_map_provided ? 'Yes' : 'No'],
          ['Synthetic map used', method.synthetic_map_used ? 'Yes (demo)' : 'No'],
        ]} />
      </div>

      {Object.entries(perTrait).map(([trait, r]) => (
        <div key={trait} className="rounded-xl border border-slate-200 bg-white" data-testid={`mol-phase2-trait-${trait}`}>
          <div className="px-4 py-2 bg-slate-50 border-b border-slate-200 text-xs font-semibold uppercase tracking-wider text-slate-600 flex items-center justify-between">
            <span>Trait — {trait}</span>
            {!r.error && (
              <span className="font-mono text-[11px] text-slate-500">
                {r.n_significant_qtls} QTL · LOD ≥ {r.lod_threshold} · {r.method}
              </span>
            )}
          </div>
          <div className="p-4 space-y-3">
            {r.error ? (
              <div className="text-amber-700 text-xs italic">{r.error}</div>
            ) : (
              <>
                <div className="overflow-x-auto">
                  <table className="w-full text-xs">
                    <thead className="bg-slate-50/50 text-slate-600">
                      <tr>
                        <th className="text-left px-2 py-1.5 w-7"></th>
                        <th className="text-left px-2 py-1.5">Marker</th>
                        <th className="text-left px-2 py-1.5">Chr</th>
                        <th className="text-right px-2 py-1.5">cM</th>
                        <th className="text-right px-2 py-1.5">LOD</th>
                        <th className="text-right px-2 py-1.5">1.5-LOD CI</th>
                        <th className="text-right px-2 py-1.5">a</th>
                        <th className="text-right px-2 py-1.5">d</th>
                        <th className="text-right px-2 py-1.5">d/|a|</th>
                        <th className="text-right px-2 py-1.5">PVE %</th>
                        <th className="text-left px-2 py-1.5">Allele dir.</th>
                        <th className="text-left px-2 py-1.5">Flanking</th>
                        <th className="text-right px-2 py-1.5">Stab.</th>
                      </tr>
                    </thead>
                    <tbody>
                      {r.qtl_peaks.length === 0 ? (
                        <tr><td colSpan={13} className="text-center px-2 py-2 italic text-slate-500">No QTL passed the threshold</td></tr>
                      ) : r.qtl_peaks.map((q, i) => {
                        const rowKey = `${trait}-${q.marker}`;
                        const isExpanded = expandedQtl === rowKey;
                        const hasCtx = !!q.context;
                        return (
                          <React.Fragment key={i}>
                            <tr
                              className={`border-t border-slate-100 ${hasCtx ? 'cursor-pointer hover:bg-indigo-50/40' : 'opacity-90'} ${isExpanded ? 'bg-indigo-50/60' : ''}`}
                              onClick={() => hasCtx && setExpandedQtl(isExpanded ? null : rowKey)}
                              data-testid={`mol-phase2-qtl-row-${q.marker}`}
                            >
                              <td className="px-2 py-1.5 text-center">
                                {hasCtx && (
                                  <span className="inline-block transition-transform" style={{ transform: isExpanded ? 'rotate(90deg)' : 'rotate(0deg)' }}>▶</span>
                                )}
                              </td>
                              <td className="px-2 py-1.5 font-mono">{q.marker}</td>
                              <td className="px-2 py-1.5">{q.chromosome}</td>
                              <td className="px-2 py-1.5 text-right tabular-nums">{q.position_cM ?? '—'}</td>
                              <td className="px-2 py-1.5 text-right tabular-nums font-bold">{q.lod_score}</td>
                              <td className="px-2 py-1.5 text-right tabular-nums">
                                {q.lod_ci_lo_cM != null && q.lod_ci_hi_cM != null
                                  ? `${q.lod_ci_lo_cM}–${q.lod_ci_hi_cM}` : '—'}
                              </td>
                              <td className="px-2 py-1.5 text-right tabular-nums">{q.additive_effect}</td>
                              <td className="px-2 py-1.5 text-right tabular-nums">{q.dominance_effect ?? '—'}</td>
                              <td className="px-2 py-1.5 text-right tabular-nums">{q.degree_of_dominance ?? '—'}</td>
                              <td className="px-2 py-1.5 text-right tabular-nums">{q.pve_percent ?? '—'}</td>
                              <td className="px-2 py-1.5 text-[10px] text-slate-600">{q.allele_direction}</td>
                              <td className="px-2 py-1.5 font-mono text-[10px]">{(q.flanking_markers || []).join(' / ')}</td>
                              <td className="px-2 py-1.5 text-right tabular-nums">{q.stability_score ?? '—'}</td>
                            </tr>
                            {isExpanded && hasCtx && (
                              <tr className="border-t border-indigo-200 bg-indigo-50/30" data-testid={`mol-phase2-qtl-context-${q.marker}`}>
                                <td colSpan={13} className="px-3 py-3">
                                  <QtlContextStrip ctx={q.context} marker={q.marker} trait={trait} />
                                </td>
                              </tr>
                            )}
                          </React.Fragment>
                        );
                      })}
                    </tbody>
                  </table>
                </div>
                {profileCharts[trait] && (
                  <div className="mt-2">
                    <img src={`data:image/png;base64,${profileCharts[trait]}`}
                      alt={`LOD profile — ${trait}`} className="max-w-full rounded-md border border-slate-200" />
                  </div>
                )}
                {r.cofactor_markers && r.cofactor_markers.length > 0 && (
                  <div className="text-[11px] text-slate-500 font-mono pt-1">
                    Cofactors selected: {r.cofactor_markers.join(', ')}
                  </div>
                )}
                {r.trait_architecture_class && (
                  <div className="text-[11px] text-slate-600">
                    Architecture: <span className="font-semibold">{r.trait_architecture_class}</span>
                    {r.sum_pve_percent != null && <> · Σ PVE = {r.sum_pve_percent}%</>}
                  </div>
                )}
              </>
            )}
          </div>
        </div>
      ))}

      <div className="rounded-xl border border-slate-200 bg-white" data-testid="mol-phase2-architecture">
        <div className="px-4 py-2 bg-slate-50 border-b border-slate-200 text-xs font-semibold uppercase tracking-wider text-slate-600 flex items-center justify-between gap-2">
          <span className="flex items-center gap-2">
            <FlaskConical size={11} /> Trait architecture (data hook for Phase 4 GS)
          </span>
          {promoteEligible && (
            <button
              type="button"
              onClick={() => promoteToPhase4(result)}
              className="text-[11px] font-bold rounded-full px-3 py-1.5 transition-all border bg-[#6366F1] text-white border-[#6366F1] hover:bg-[#4F46E5] shadow-sm"
              data-testid="mol-phase2-promote-button"
              title="Pre-fill Phase 4 (Genomic Selection) with these major QTL as fixed effects"
            >
              → Use these QTL in Phase 4 prediction
            </button>
          )}
        </div>
        <table className="w-full text-xs">
          <thead className="bg-slate-50/50 text-slate-600">
            <tr>
              <th className="text-left px-3 py-1.5">Trait</th>
              <th className="text-left px-3 py-1.5">Class</th>
              <th className="text-right px-3 py-1.5"># QTL</th>
              <th className="text-right px-3 py-1.5">Σ PVE %</th>
              <th className="text-left px-3 py-1.5">Major QTL markers</th>
            </tr>
          </thead>
          <tbody>
            {Object.entries(result.trait_architecture || {}).map(([trait, a]) => (
              <tr key={trait} className="border-t border-slate-100">
                <td className="px-3 py-1.5">{trait}</td>
                <td className="px-3 py-1.5">{a.class}</td>
                <td className="px-3 py-1.5 text-right tabular-nums">{a.n_qtl}</td>
                <td className="px-3 py-1.5 text-right tabular-nums">{a.sum_pve ?? '—'}</td>
                <td className="px-3 py-1.5 font-mono text-[10px]">{(a.major_qtl_markers || []).join(', ') || '—'}</td>
              </tr>
            ))}
          </tbody>
        </table>
      </div>
    </div>
  );
}


/**
 * BATCH-NEXT-3 (Niranjan, Feb 2026) — QTL Context Strip.
 *
 * Renders the three pre-computed sub-panels for a single QTL row:
 *   1. ±5 cM zoomed LOD curve
 *   2. ±10-marker LD ladder
 *   3. Genotype-class boxplot at the peak marker
 * + sharpness / mean-LD diagnostics + interpretation hint.
 */

/**
 * Sub-Batch 3AE (Feb 8 2026): SMA → SIM → CIM overlay panel.
 * For each trait that has a `methods_comparison` block, render
 * three overlaid LOD curves per chromosome on a tiny inline SVG
 * (no D3 / chart library dependency — vanilla SVG keeps the
 * payload small and the render predictable).
 */
function Phase2MethodsComparisonPanel({ perTrait }) {
  const traitsWithComparison = Object.entries(perTrait || {}).filter(
    ([, r]) => r && r.methods_comparison
  );
  if (traitsWithComparison.length === 0) return null;

  // Colour-coded by method (Okabe-Ito subset for consistency
  // with the Figure B palette used elsewhere on the platform).
  const METHOD_COLOURS = {
    SMA: '#56B4E9',
    SIM: '#E69F00',
    CIM: '#009E73',
  };

  return (
    <div
      className="rounded-2xl border border-[#E8E6E1] overflow-hidden"
      data-testid="mol-phase2-methods-comparison"
    >
      <div className="bg-[#4338CA]/8 px-4 py-2.5 border-b border-[#E8E6E1]">
        <h4 className="font-semibold text-sm text-[#4338CA] uppercase tracking-wider">
          Method Comparison · SMA → SIM → CIM
        </h4>
        <p className="text-[11px] text-slate-500 mt-0.5 leading-relaxed">
          Three LOD profiles overlaid per chromosome on the same
          1 cM grid. SMA (light blue) is per-marker single
          regression — spiky, no interval information. SIM
          (orange) smooths it via flanking-marker conditional
          probabilities (Lander-Botstein 1989). CIM (green)
          tightens further by regressing out background QTL on
          other chromosomes (Zeng 1994; Jansen 1993). The dashed
          horizontal line is the Bonferroni overlay threshold —
          rough but consistent across methods; gold-standard
          Churchill-Doerge thresholds are used for the primary
          method&apos;s peak detection above.
        </p>
      </div>
      <div className="px-4 py-4 space-y-5">
        {traitsWithComparison.map(([trait, r]) => {
          const cmp = r.methods_comparison || {};
          const bonferroni = cmp.bonferroni_overlay_threshold || 3.0;
          // Collect the set of chromosomes present in any method
          const chrSet = new Set();
          ['SMA', 'SIM', 'CIM'].forEach((m) => {
            const grid = cmp[m] && cmp[m].lod_profile_grid;
            if (grid && grid.chromosome) {
              grid.chromosome.forEach((c) => chrSet.add(String(c)));
            }
          });
          const chroms = Array.from(chrSet).sort((a, b) => {
            if (a.length !== b.length) return a.length - b.length;
            return a < b ? -1 : 1;
          });
          // Determine global max LOD for consistent y-axis
          let maxLod = bonferroni * 1.4;
          ['SMA', 'SIM', 'CIM'].forEach((m) => {
            const grid = cmp[m] && cmp[m].lod_profile_grid;
            if (grid && grid.lod) {
              grid.lod.forEach((v) => {
                if (v > maxLod) maxLod = v;
              });
            }
          });
          // Per chromosome, build the 3 curves as inline SVG
          return (
            <div key={trait} data-testid={`mol-phase2-cmp-${trait}`}>
              <div className="flex items-center gap-3 mb-2">
                <h5 className="font-semibold text-[13px] text-slate-700">
                  {trait}
                </h5>
                <div className="flex items-center gap-3 text-[11px] text-slate-500">
                  {['SMA', 'SIM', 'CIM'].map((m) => (
                    <span key={m} className="flex items-center gap-1.5">
                      <span
                        className="inline-block w-3 h-0.5"
                        style={{ background: METHOD_COLOURS[m] }}
                      />
                      <span className="font-mono">
                        {m}
                        {cmp[m] && cmp[m].peak_lod !== undefined && (
                          <span className="text-slate-400 ml-1">
                            (max={cmp[m].peak_lod.toFixed(1)})
                          </span>
                        )}
                      </span>
                    </span>
                  ))}
                  <span className="text-slate-400">
                    · Bonferroni overlay: {bonferroni.toFixed(2)}
                  </span>
                </div>
              </div>
              <div
                className="grid gap-2"
                style={{
                  gridTemplateColumns: `repeat(${Math.min(chroms.length, 5)}, minmax(0, 1fr))`,
                }}
              >
                {chroms.map((ch) => (
                  <ChromosomePanel
                    key={`${trait}-${ch}`}
                    chromosome={ch}
                    methodsData={cmp}
                    maxLod={maxLod}
                    bonferroni={bonferroni}
                    colours={METHOD_COLOURS}
                  />
                ))}
              </div>
            </div>
          );
        })}
      </div>
    </div>
  );
}

function ChromosomePanel({ chromosome, methodsData, maxLod, bonferroni, colours }) {
  const W = 220;
  const H = 110;
  const PAD = { l: 28, r: 6, t: 8, b: 18 };
  const plotW = W - PAD.l - PAD.r;
  const plotH = H - PAD.t - PAD.b;

  // Extract per-method positions and LOD on this chromosome
  const series = ['SMA', 'SIM', 'CIM'].map((m) => {
    const grid = methodsData[m] && methodsData[m].lod_profile_grid;
    if (!grid) return { method: m, x: [], y: [] };
    const x = [];
    const y = [];
    grid.chromosome.forEach((c, i) => {
      if (String(c) === chromosome) {
        x.push(grid.position_cM[i]);
        y.push(grid.lod[i]);
      }
    });
    return { method: m, x, y };
  });
  // Find x-axis range
  let xmin = Infinity;
  let xmax = -Infinity;
  series.forEach((s) => {
    s.x.forEach((v) => {
      if (v < xmin) xmin = v;
      if (v > xmax) xmax = v;
    });
  });
  if (!isFinite(xmin) || !isFinite(xmax) || xmax <= xmin) {
    xmin = 0;
    xmax = 100;
  }
  const xScale = (v) => PAD.l + ((v - xmin) / (xmax - xmin)) * plotW;
  const yScale = (v) => PAD.t + plotH - (v / maxLod) * plotH;

  const pathFor = (s) => {
    if (s.x.length < 2) return '';
    const pts = s.x.map((xv, i) => `${i === 0 ? 'M' : 'L'}${xScale(xv)},${yScale(s.y[i])}`);
    return pts.join(' ');
  };

  return (
    <div className="rounded-lg border border-[#E8E6E1] bg-white p-1">
      <svg
        viewBox={`0 0 ${W} ${H}`}
        preserveAspectRatio="xMidYMid meet"
        className="w-full h-auto"
        data-testid={`mol-phase2-cmp-svg-${chromosome}`}
      >
        {/* y-axis ticks */}
        {[0, 0.25, 0.5, 0.75, 1.0].map((f) => {
          const ytick = maxLod * f;
          return (
            <g key={f}>
              <line
                x1={PAD.l}
                x2={W - PAD.r}
                y1={yScale(ytick)}
                y2={yScale(ytick)}
                stroke="#E5E7EB"
                strokeWidth="0.5"
              />
              <text
                x={PAD.l - 3}
                y={yScale(ytick) + 3}
                fontSize="6"
                fill="#6B7280"
                textAnchor="end"
              >
                {ytick.toFixed(1)}
              </text>
            </g>
          );
        })}
        {/* Bonferroni threshold dashed line */}
        <line
          x1={PAD.l}
          x2={W - PAD.r}
          y1={yScale(bonferroni)}
          y2={yScale(bonferroni)}
          stroke="#000"
          strokeDasharray="3,2"
          strokeWidth="0.7"
        />
        {/* x-axis */}
        <line
          x1={PAD.l}
          x2={W - PAD.r}
          y1={PAD.t + plotH}
          y2={PAD.t + plotH}
          stroke="#9CA3AF"
          strokeWidth="0.6"
        />
        {/* curves */}
        {series.map((s) => (
          <path
            key={s.method}
            d={pathFor(s)}
            fill="none"
            stroke={colours[s.method]}
            strokeWidth="1.1"
            strokeLinejoin="round"
          />
        ))}
        {/* chromosome label */}
        <text
          x={W / 2}
          y={H - 4}
          fontSize="8"
          fill="#1B4332"
          textAnchor="middle"
          fontWeight="600"
        >
          Chr {chromosome}
        </text>
      </svg>
    </div>
  );
}


function QtlContextStrip({ ctx, marker, trait }) {
  if (!ctx) return null;
  const tooltip = (text) => (
    <span className="text-slate-400 cursor-help" title={text}>ⓘ</span>
  );
  const sharpness = ctx.sharpness_score;
  const meanLd = ctx.mean_ld_to_flanks;
  return (
    <div className="space-y-3" data-testid={`mol-phase2-context-strip-${marker}`}>
      <div className="grid grid-cols-1 lg:grid-cols-3 gap-3">
        <ContextPanel
          title={`Zoomed LOD profile`}
          subtitle={`±5 cM around ${marker}`}
          b64={ctx.zoomed_lod_chart}
          testid={`mol-phase2-ctx-zoomed-${marker}`}
        />
        <ContextPanel
          title={`Flanking-marker LD ladder`}
          subtitle={`±10 markers (pairwise r²)`}
          b64={ctx.ld_ladder_chart}
          testid={`mol-phase2-ctx-ld-${marker}`}
        />
        <ContextPanel
          title={`Genotype-class boxplot`}
          subtitle={`${trait} stratified by ${marker} dosage`}
          b64={ctx.genotype_class_boxplot}
          testid={`mol-phase2-ctx-boxplot-${marker}`}
        />
      </div>
      <div
        className="rounded-md px-3 py-2 text-xs leading-relaxed"
        style={{
          background: ctx.interpretation?.startsWith('✓') ? '#DCFCE7'
            : ctx.interpretation?.startsWith('⚠') ? '#FEF3C7' : '#EEF2FF',
          border: '1px solid ' + (ctx.interpretation?.startsWith('✓') ? '#16A34A40'
            : ctx.interpretation?.startsWith('⚠') ? '#F59E0B40' : '#6366F140'),
          color: '#1E293B',
        }}
        data-testid={`mol-phase2-ctx-interpretation-${marker}`}
      >
        <div className="font-semibold mb-1.5">{ctx.interpretation}</div>
        <div className="flex flex-wrap gap-x-5 gap-y-1 text-[11px] text-slate-600">
          <span>
            Sharpness score = <span className="font-mono font-semibold">{sharpness != null ? sharpness.toFixed(3) : '—'}</span>
            {' '}{tooltip('(peak_LOD − LOD_at_2cM_away) / peak_LOD. >0.5 = sharp, <0.2 = broad.')}
          </span>
          <span>
            Mean flanking r² = <span className="font-mono font-semibold">{meanLd != null ? meanLd.toFixed(3) : '—'}</span>
            {' '}{tooltip('Average pairwise r² between peak marker and ±10 flanking markers. <0.5 = low LD, >0.7 = high LD.')}
          </span>
        </div>
      </div>
    </div>
  );
}


function ContextPanel({ title, subtitle, b64, testid }) {
  return (
    <div className="rounded-md border border-slate-200 bg-white p-2" data-testid={testid}>
      <div className="text-[11px] font-semibold text-slate-700 mb-0.5">{title}</div>
      <div className="text-[10px] text-slate-500 mb-1.5">{subtitle}</div>
      {b64 ? (
        <img src={`data:image/png;base64,${b64}`} alt={title} className="w-full rounded" />
      ) : (
        <div className="text-[11px] italic text-slate-400 px-2 py-3">Panel unavailable</div>
      )}
    </div>
  );
}


/**
 * BATCH-NEXT-3 — "Promote to Fixed-QTL" handoff.
 *
 * Persists trait_architecture + recommended PVE threshold into
 * sessionStorage with an `auto_promoted` flag, then navigates to
 * /molecular-genetics with `?phase=4` so the Phase 4 upload card
 * picks up the import on mount.
 */
function promoteToPhase4(result) {
  const arch = result?.trait_architecture || {};
  // Recommended PVE threshold = floor of min PVE across major QTL,
  // snapped to 5 % step, clamped to [1, 10] (must match backend).
  const recsPerTrait = Object.entries(arch).map(([trait, a]) => {
    const pves = (a?.qtl_peaks || [])
      .map(q => q?.pve_percent)
      .filter(v => typeof v === 'number');
    if (!pves.length) return { trait, recommended: a?.recommended_pve_threshold ?? 5.0 };
    return { trait, recommended: a?.recommended_pve_threshold ?? 5.0 };
  });
  const overall = recsPerTrait.length
    ? Math.min(...recsPerTrait.map(r => r.recommended))
    : 5.0;
  const payload = {
    auto_promoted: true,
    timestamp: Date.now(),
    trait_architecture: arch,
    recommended_pve_threshold: overall,
    eligible_traits: Object.keys(arch),
  };
  try {
    sessionStorage.setItem('bk_phase4_promote', JSON.stringify(payload));
  } catch (e) {
    /* sessionStorage unavailable — fail silently, no blocker */
  }
  // Navigate via window.location since this component is a render
  // helper, not a router-aware page. Using ?phase=4 so the parent
  // Molecular Genetics page can land on the Phase 4 tab.
  if (typeof window !== 'undefined') {
    window.location.href = '/molecular-genetics?phase=4';
  }
}


function SummaryCard({ title, rows, testid }) {
  return (
    <div className="rounded-xl border border-slate-200 bg-white" data-testid={testid}>
      <div className="px-4 py-2 bg-slate-50 border-b border-slate-200 text-xs font-semibold uppercase tracking-wider text-slate-600">
        {title}
      </div>
      <dl className="divide-y divide-slate-100">
        {rows.map(([k, v], i) => (
          <div key={i} className="px-4 py-1.5 grid grid-cols-2 text-sm">
            <dt className="text-slate-500 text-xs">{k}</dt>
            <dd className="text-right text-slate-800 tabular-nums font-mono text-xs">{v ?? '—'}</dd>
          </div>
        ))}
      </dl>
    </div>
  );
}
